import { UnleashFeatureFlagsContext, UseUnleashFeatureFlags } from '@forto-dev/frontend-infra-unleash'
import { HttpError } from '@forto-dev/ui-platform-http'
import { FeatureFlagResponse as FeatureFlag } from '@freighthub/user-profile-core-client'
import { useUserProfileCoreApi } from 'features/ShipmentsList'
import { values } from 'lodash-es'
import { useObserver } from 'mobx-react-lite'
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { logError } from 'utils/logger'

import { useAuth } from './useAuth'

export {
  useUnleashFeatureFlags,
  UseUnleashFeatureFlags,
  UnleashFeatureFlagsContext,
} from '@forto-dev/frontend-infra-unleash'

export enum UnleashFeatureFlagKeys {
  BOOKINGS_EXCLUDE_CUSTOMERS = 'bookings.excluded-customers',
  QUOTE_REQUEST_FCL_CRD = 'ship.quote-request-fcl-crd',
  SHOW_DEFAULT_FOR_INTEGRATIONS_IN_LEGAL_ENTITY = 'cftmasterdata.showDefaultForIntegrationsInLEs',
  LOCAL_CURRENCY_DISPLAY = 'cft-booking.ship.local-currency-display',
  NEW_RATE_FILTERS = 'cft-booking.ship.new-rate-filters',
  BOOKING_ACCEPTANCE_REQUIREMENTS = 'tms.booking.acceptance-requirements',

  // FFs owned by Insights And Integrations
  DEM_DET_BLACKLIST_RELEASE = 'cft-insights.releases.dem_det_blacklist', // TODO: remove after verifying that SHIPMENT_OVERVIEW_DEM_DET_TAB is not in use
  DEM_DET = 'cft-insights.mot.dem_det', // Operational FF to blacklist customers from using DEM/DET
  SUPPLIER_BOOKING_REQUEST = 'ship.insights-integrations.supplier-booking-request',
  EMISSIONS_REPORT = 'init.emissionReportDisable',
  FEED_V2_MESSAGE_ENDPOINT = 'insights-integrations.releases.feedV2Messaging',

  SHIPMENT_OVERVIEW_DEM_DET_TAB = 'cft-shipping.ship.shipment-overview-demdet_tab',
  TRANSPORT_PLAN_PORTS = 'transport-network.ports',
  TRADE_FINANCE_BANNER = 'ship.trade-finance-banner',
  INTEGRATIONS_ENABLED = 'cftcustomersintegrations.newui',
  ALLOW_SYRIA_IN_BOOKING_RESTRICTED_COUNTRIES = 'cft-booking.ship.allow-syria-in-restricted-countries',
  SHIPMENT_IOT_TRACKING_TAB = 'cft-shipping.ship.iot-tracking',
  ENABLE_BILLING_CORE_INVOICE = 'billing.enable.billing-core.invoice',
  DOCUMENTS_V2 = 'paw.ship.documents-v2',
  SHIPMENT_TAGS = 'tms.shipment.tags',

  SHIP_V2_BETA = 'ship-v2.release.beta',
}

export const UnleashFeatureFlagsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  return useObserver(() => {
    const [isLoading, setIsLoading] = useState(false)
    const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([])
    const {
      featureFlags: { getFeatureFlags },
    } = useUserProfileCoreApi()
    const { state: authState } = useAuth()

    const loadFeatureFlags = async () => {
      try {
        setIsLoading(true)
        const featureFlagsResponse = await getFeatureFlags({ names: values(UnleashFeatureFlagKeys) })
        setFeatureFlags(featureFlagsResponse)
      } catch (e) {
        /** Temporary workaround to clean unauthorized errors from logs
         * @todo Move all error logging to HTTP service */
        if ((e as HttpError)?.status !== 401) {
          // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
          logError(e)
        }
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      if (!authState.authenticated) return
      loadFeatureFlags()
    }, [authState.authenticated])

    const state: UseUnleashFeatureFlags = useMemo(() => {
      const isEnabled = (featureFlagKey: string): boolean =>
        Boolean(featureFlags.find((featureFlag) => featureFlag.name === featureFlagKey)?.enabled)

      return {
        featureFlags,
        isEnabled,
        isFFLoading: isLoading,
      }
    }, [featureFlags, isLoading])

    return <UnleashFeatureFlagsContext.Provider value={state}>{children}</UnleashFeatureFlagsContext.Provider>
  })
}
